import React from 'react';

const DeepDive: React.FC = () => {
  const fenPosition = "r3r1k1/pb3p1p/2pBp1p1/3n4/2B1N3/8/Pb3PPP/3RR1K1 w - - 0 20";
  
  return (
    <div 
      className="border border-terminal-green/30 rounded-lg p-6 bg-bg-dark"
    >
      <h3 className="text-xl font-vt323 mb-4 text-terminal-green">Perché è sorprendente che un modello linguistico giochi a scacchi?</h3>
      
      <div className="text-sm space-y-4">
        <p className="text-terminal-green/90">
          Può sembrare naturale che un'intelligenza artificiale giochi bene a scacchi, ma i modelli linguistici (LLM) come GPT o Gemini non sono affatto adatti per questo compito. Ecco perché la loro abilità sorprende anche gli esperti di IA e scacchi.
        </p>
        
        <div className="border-t border-terminal-green/30 my-4"></div>
        
        <h4 className="text-lg font-vt323 text-terminal-green">IA per scacchi vs IA per testo: due mondi diversi</h4>
        <ul className="list-disc pl-5 space-y-2 text-terminal-green/90">
          <li>Stockfish e AlphaZero sono progettati specificamente per il gioco degli scacchi. Utilizzano regole chiare, valutano milioni di mosse e pianificano strategie avanzate.</li>
          <li>GPT e simili sono stati creati esclusivamente per predire e generare testo, leggendo milioni di testi, inclusi libri, articoli e partite di scacchi annotate in modo testuale.</li>
        </ul>
        <blockquote className="border-l-2 border-terminal-green pl-3 italic text-terminal-green/80 my-2">
          Non ragionano visualizzando la scacchiera, ma completano frasi come "White to move. Best move: ..."
        </blockquote>

        <div className="mt-6 mb-2">
          <p className="text-terminal-green/90 mb-2 font-vt323">$ PROMPT TESTUALE:</p>
          <div className="bg-black/40 p-3 rounded border border-terminal-green/30 font-mono text-sm">
            <p className="text-terminal-green/90">Stai giocando con i Bianchi. Quale mossa giochi?</p>
            <p className="text-terminal-green/90 truncate">FEN: {fenPosition}</p>
            <p className="text-terminal-green/90">Ultima mossa: g7b2</p>
          </div>
        </div>

        <div className="mb-6">
          <p className="text-terminal-green/90 mb-2 font-vt323">$ PROMPT VISIVO:</p>
          <div className="bg-black/40 p-3 rounded border border-terminal-green/30 font-mono text-sm">
            <div className="">
              <p className="text-terminal-green/90">Stai giocando con i Bianchi. Quale mossa giochi?</p>
              <p className="text-terminal-green/90">Ultima mossa: g7b2</p>
            </div>
            <div className="mt-3 w-full flex justify-center">
              <div className="w-[180px] h-[180px] border border-terminal-green/50 flex items-center justify-center">
                <img 
                  src="/chess-position-example.png" 
                  alt="Chess position" 
                  className="max-w-full max-h-full"
                />
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-black/40 p-3 rounded border border-terminal-green/30 font-mono text-sm">
          <p className="text-terminal-green/90 font-bold">Risposta del modello: "d1b1"</p>
        </div>
        
        <div className="border-t border-terminal-green/30 my-4"></div>
        
        <h4 className="text-lg font-vt323 text-terminal-green">Come funziona davvero un LLM negli scacchi?</h4>
        <p className="text-terminal-green/90">
          Quando sfidi un modello linguistico a scacchi, non sta facendo calcoli profondi o strategici. Sta semplicemente completando del testo, cercando di prevedere quale mossa potrebbe essere statisticamente più probabile basandosi sui dati che ha letto. È come un pappagallo che ripete "1. e4 e5 2. Nf3 Nc6 3. Bb5" perché l'ha sentita molte volte. Sorprendentemente, a volte riesce a scegliere mosse corrette persino in situazioni mai incontrate prima.
        </p>
        
        <div className="mt-3 w-full flex justify-center">
          <div className="w-[270px] h-[180px] border border-terminal-green/50 flex items-center justify-center">
            <img 
              src="/parrots-deepdive.png" 
              alt="Parrots deepdive" 
              className="max-w-full max-h-full"
            />
          </div>
        </div>
        
        <div className="border-t border-terminal-green/30 my-4"></div>
        
        <h4 className="text-lg font-vt323 text-terminal-green">Comportamento emergente: cosa significa?</h4>
        <p className="text-terminal-green/90">
          Questi modelli non conoscono esplicitamente le regole degli scacchi. Nessuno gli ha insegnato che i pedoni si promuovono solo arrivando all'ultima riga o che i cavalli saltano sopra gli altri pezzi senza muoversi in linea retta. Eppure spesso riescono a:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-terminal-green/90">
          <li>Effettuare un arrocco corretto</li>
          <li>Evitare mosse illegali</li>
          <li>Trovare scacco matto in una mossa</li>
        </ul>
        <p className="text-terminal-green/90">
          Questo fenomeno, noto come comportamento emergente, è sorprendente perché mostra capacità di ragionamento apparente in sistemi che teoricamente non dovrebbero averle.
        </p>
        
        <div className="border-t border-terminal-green/30 my-4"></div>
        
        <h4 className="text-lg font-vt323 text-terminal-green">Perché tutto questo è così affascinante?</h4>
        <p className="text-terminal-green/90">
          Assistere a un modello linguistico che ti batte a scacchi è affascinante perché sfida la nostra idea di cosa significhi realmente "ragionare". È come vedere un pappagallo che improvvisamente impara non solo a imitare il linguaggio, ma anche a risolvere problemi complessi, senza aver mai studiato esplicitamente le regole. Questo fenomeno ci costringe a ripensare la distinzione tra sistemi simbolici (basati su regole esplicite) e sistemi non simbolici (come i modelli linguistici, basati su pattern statistici). Tutto ciò solleva una domanda ancora più profonda:
        </p>
        <blockquote className="border-l-2 border-terminal-green pl-3 italic text-terminal-green/80 my-2">
          Che cosa significa veramente ragionare? E come cambierà questa comprensione il futuro dell'intelligenza artificiale e degli scacchi?
        </blockquote>
      </div>
    </div>
  );
};

export default DeepDive;